import * as Joi from "joi";
import { useCallback, useMemo, useState } from "react";
import { Flex, Tabs } from "@radix-ui/themes";

import { useExploreInteractions } from "@/v2/entities/explore/Explore.api";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { InteractionType } from "@/routes/simulator-live-mode/types";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { SimulatorChannel } from "@/v2/entities/simulator/Simulator.model";

import { useDraft } from "../../context/useDraft";
import { useSimulator } from "../../context/SimulatorWrapper";
import { useChat } from "../../context/ChatWrapper";
import styles from "../../styles.module.scss";

import {
  topicToTopicWithBadges,
  TopicWithBadges,
  NavigationView,
  navigationViews,
} from "./TopicsList.types";
import { TopicType } from "./TopicsList.shared.types";
import { TopicsView } from "./TopicsView";
import { PendingReviewView } from "./PendingReviewView";
import { TrainingTranscript } from "./TrainingTranscript";
import { InteractionView } from "./InteractionView";
import { SessionsView } from "./SessionsView";

interface TopicsListProps {
  isLoadingMessages: boolean;
}

export const TopicsList = ({ isLoadingMessages }: TopicsListProps) => {
  const { simulator, topics: topicsList } = useSimulator();
  const { selectedTopic, handleTopicSelect } = useChat();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<TopicType>("all");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ExploreFiltersValue>({});
  const [activeTab, setActiveTab] = useState<NavigationView>("topics");
  const [previousTab, setPreviousTab] = useState<NavigationView>("topics");
  const [selectedInteractionType, setSelectedInteractionType] =
    useState<ExploreInteractionType>("tickets");
  const [previousInteractionType, setPreviousInteractionType] =
    useState<ExploreInteractionType>("tickets");
  const [interactionSearch, setInteractionSearch] = useState("");
  const [pendingReviewSearch, setPendingReviewSearch] = useState("");
  const [sessionsSearch, setSessionsSearch] = useState("");
  const [
    selectedPendingReviewInteractionType,
    setSelectedPendingReviewInteractionType,
  ] = useState<InteractionType>(
    simulator?.channel === SimulatorChannel.autonomous_quackchat
      ? InteractionType.CONVERSATION
      : InteractionType.TICKET
  );
  const [
    previousPendingReviewInteractionType,
    setPreviousPendingReviewInteractionType,
  ] = useState<InteractionType>(
    simulator?.channel === SimulatorChannel.autonomous_quackchat
      ? InteractionType.CONVERSATION
      : InteractionType.TICKET
  );
  const isDisabled = isLoadingMessages;

  const [showTranscript, setShowTranscript] = useState<{
    interactionId: string;
    fetchTranscriptId: string;
    interactionType: InteractionType;
    pendingReviewId?: number;
  } | null>(null);

  const sharedFilter = useMemo<Filter | undefined>(() => {
    const activeFiltersKeys = Object.keys(activeFilters);
    if (activeFiltersKeys.length === 0) {
      return undefined;
    }

    const filterConditions: Filter[] = [];

    Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue && "operator" in filterValue) {
        filterConditions.push({
          [field]: { [filterValue.operator]: filterValue.value },
        });
      }
    });

    if (filterConditions.length === 0) {
      return undefined;
    }

    return {
      and: filterConditions,
    };
  }, [activeFilters]);

  const { data: ticketsData, isLoading: isLoadingTickets } =
    useExploreInteractions({
      source: "tickets",
      sorting: [{ id: "createdAt", desc: true }],
      filter: sharedFilter,
      enabled: activeTab === "interactions",
    });

  const { topics } = useDraft();

  const topicsWithBadges: TopicWithBadges[] = useMemo(() => {
    return topics.map((topicStatus) => topicToTopicWithBadges(topicStatus));
  }, [topics]);

  const resetNavigation = useCallback(() => {
    setActiveTab(previousTab);
    if (previousTab === "interactions") {
      setSelectedInteractionType(previousInteractionType);
    } else if (previousTab === "pending-review") {
      setSelectedPendingReviewInteractionType(
        previousPendingReviewInteractionType
      );
    }
    setShowTranscript(null);
  }, [
    previousTab,
    previousInteractionType,
    previousPendingReviewInteractionType,
  ]);

  const handleNavigateToTranscript = useCallback(
    (
      interactionId: string,
      fetchTranscriptId: string,
      interactionType: InteractionType,
      pendingReviewId?: number
    ) => {
      setPreviousTab(activeTab);
      if (activeTab === "interactions") {
        setPreviousInteractionType(selectedInteractionType);
      } else if (activeTab === "pending-review") {
        setPreviousPendingReviewInteractionType(
          selectedPendingReviewInteractionType
        );
      }
      setShowTranscript({
        interactionId,
        fetchTranscriptId,
        interactionType,
        pendingReviewId,
      });
    },
    [activeTab, selectedInteractionType, selectedPendingReviewInteractionType]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      setPreviousTab(activeTab);
      setActiveTab(
        Joi.string()
          .valid(...navigationViews)
          .validate(value).value
      );
    },
    [activeTab]
  );

  const handleInteractionTypeChange = useCallback(
    (type: ExploreInteractionType) => {
      setSelectedInteractionType(type);
    },
    []
  );

  const handlePendingReviewInteractionTypeChange = useCallback(
    (type: InteractionType) => {
      setSelectedPendingReviewInteractionType(type);
    },
    []
  );

  const onTopicSelect = useCallback(
    (topic: string | null) => {
      if (!topicsList) return;

      const topicObj = topicsList.find((t) => t.topic === topic);
      handleTopicSelect(topicObj || null);

      if (topic) {
        setActiveFilters((prev) => {
          const existingTopicFilter = prev.topic;

          if (
            existingTopicFilter &&
            existingTopicFilter.operator === "in" &&
            Array.isArray(existingTopicFilter.value)
          ) {
            const existingTopics = existingTopicFilter.value as string[];
            if (!existingTopics.includes(topic)) {
              return {
                ...prev,
                topic: {
                  operator: "in",
                  value: [...existingTopics, topic],
                },
              };
            }
            return prev;
          } else {
            return {
              ...prev,
              topic: {
                operator: "in",
                value: [topic],
              },
            };
          }
        });
      } else {
        setActiveFilters((prev) => {
          const { topic, ...rest } = prev;
          return rest;
        });
      }
    },
    [handleTopicSelect]
  );

  return (
    <Flex direction="column" p={"2"} className={styles.topicsListContainer}>
      {!showTranscript ? (
        <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Trigger value="topics">Topics</Tabs.Trigger>
            <Tabs.Trigger value="interactions">Interactions</Tabs.Trigger>
            <Tabs.Trigger value="pending-review">Pending Review</Tabs.Trigger>
            <Tabs.Trigger value="sessions">Sessions</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="topics">
            <TopicsView
              topics={topicsWithBadges}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              selectedTopic={selectedTopic}
              onTopicSelect={onTopicSelect}
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              isDisabled={isDisabled}
            />
          </Tabs.Content>

          <Tabs.Content value="interactions">
            <InteractionView
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              ticketsData={ticketsData}
              isLoadingTickets={isLoadingTickets}
              onNavigateToTranscript={handleNavigateToTranscript}
              onResetNavigation={resetNavigation}
              sharedFilter={sharedFilter}
              selectedInteractionType={selectedInteractionType}
              onInteractionTypeChange={handleInteractionTypeChange}
              search={interactionSearch}
              onSearchChanged={setInteractionSearch}
              topics={topicsList || undefined}
            />
          </Tabs.Content>

          <Tabs.Content value="pending-review">
            <PendingReviewView
              selectedTopic={selectedTopic}
              topicIds={
                selectedTopic
                  ? topics
                      .filter(
                        (topic) => topic.topicName === selectedTopic?.topic
                      )
                      .map((topic) => topic.topicId)
                  : topics.map((topic) => topic.topicId)
              }
              onNavigateToTranscript={handleNavigateToTranscript}
              onResetNavigation={resetNavigation}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              sharedFilter={sharedFilter}
              selectedInteractionType={selectedPendingReviewInteractionType}
              onInteractionTypeChange={handlePendingReviewInteractionTypeChange}
              search={pendingReviewSearch}
              onSearchChanged={setPendingReviewSearch}
            />
          </Tabs.Content>

          <Tabs.Content value="sessions">
            <SessionsView
              selectedTopic={selectedTopic}
              topicIds={
                selectedTopic
                  ? topics
                      .filter(
                        (topic) => topic.topicName === selectedTopic?.topic
                      )
                      .map((topic) => topic.topicId)
                  : topics.map((topic) => topic.topicId)
              }
              onNavigateToTranscript={handleNavigateToTranscript}
              onResetNavigation={resetNavigation}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              sharedFilter={sharedFilter}
              search={sessionsSearch}
              onSearchChanged={setSessionsSearch}
            />
          </Tabs.Content>
        </Tabs.Root>
      ) : (
        <TrainingTranscript
          showTranscript={showTranscript}
          handleBack={resetNavigation}
        />
      )}
    </Flex>
  );
};
